import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Check, X, Loader2, UserCheck } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import ApproveAccessDialog from "@/components/ApproveAccessDialog";

export const ACCESS_CHANGED = "pd-access-requests-changed";
const fmt = (s) => (s ? new Date(s).toLocaleString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "–");

function RequestRow({ r, onDecide, busy }) {
  return (
    <li className="flex flex-col sm:flex-row sm:items-center gap-3 py-4" data-testid={`access-request-${r.id}`}>
      <div className="flex items-center gap-3 flex-1 min-w-0">
        {r.picture ? <img src={r.picture} alt="" className="h-11 w-11 rounded-full object-cover shrink-0 bg-slate-200" referrerPolicy="no-referrer" onError={(e) => { e.currentTarget.style.visibility = "hidden"; }} />
          : <div className="h-11 w-11 rounded-full bg-slate-200 shrink-0" />}
        <div className="min-w-0">
          <div className="font-semibold truncate">{r.name}</div>
          <div className="text-sm text-slate-500 truncate">{r.email}</div>
          <div className="text-xs text-slate-400 mt-0.5">
            Angefragt {fmt(r.created_at)}{r.decided_at && ` · Entschieden ${fmt(r.decided_at)}${r.decided_by_name ? ` (${r.decided_by_name})` : ""}`}
          </div>
          {r.status === "approved" && r.approved_into && (
            <div className="text-xs text-green-700 mt-0.5" data-testid={`access-approved-into-${r.id}`}>
              {r.approved_into.mode === "join" ? `Mitarbeiter bei ${r.approved_into.workspace_label} · Rolle ${r.approved_into.role_label || r.approved_into.role}` : "Eigene Firma"}
            </div>
          )}
        </div>
      </div>
      <div className="flex gap-2 shrink-0">
        {r.status !== "approved" && (
          <Button size="sm" className="bg-green-600 hover:bg-green-700" disabled={busy} onClick={() => onDecide(r, "approve")} data-testid={`access-approve-${r.id}`}>
            <Check className="h-4 w-4 mr-1" />Freigeben
          </Button>
        )}
        {r.status !== "rejected" && (
          <Button size="sm" variant="outline" className="text-red-600 border-red-200 hover:bg-red-50" disabled={busy} onClick={() => onDecide(r, "reject")} data-testid={`access-reject-${r.id}`}>
            <X className="h-4 w-4 mr-1" />{r.status === "approved" ? "Sperren" : "Ablehnen"}
          </Button>
        )}
      </div>
    </li>
  );
}

export default function AccessRequests() {
  const [tab, setTab] = useState("pending");
  const [items, setItems] = useState(null);
  const [busy, setBusy] = useState(false);
  const [approving, setApproving] = useState(null);
  const load = useCallback(() => {
    setItems(null);
    api.get(`/access-requests?status=${tab}`).then((r) => setItems(r.data)).catch(() => setItems([]));
  }, [tab]);
  useEffect(() => { load(); }, [load]);

  const decide = async (r, action, body) => {
    if (action === "approve" && !body) { setApproving(r); return; }
    if (action === "reject" && r.status === "approved" && !window.confirm(`Konto von ${r.email} sperren? Daten bleiben erhalten.`)) return;
    setBusy(true);
    try {
      await api.post(`/access-requests/${r.id}/${action}`, body);
      setApproving(null);
      toast.success(action === "approve" ? "Zugang freigegeben" : r.status === "approved" ? "Konto gesperrt" : "Anfrage abgelehnt");
      window.dispatchEvent(new Event(ACCESS_CHANGED));
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    setBusy(false);
  };

  return (
    <div className="max-w-3xl" data-testid="access-requests-page">
      <h1 className="text-4xl sm:text-5xl font-head font-extrabold tracking-tight flex items-center gap-3">
        <UserCheck className="h-9 w-9 text-primary" />Zugangsanfragen
      </h1>
      <p className="text-slate-500 mt-2 mb-6">Neue Google-Anmeldungen bekommen erst nach Ihrer Freigabe ein Konto.</p>
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="pending" data-testid="access-tab-pending">Offen</TabsTrigger>
          <TabsTrigger value="approved" data-testid="access-tab-approved">Freigegeben</TabsTrigger>
          <TabsTrigger value="rejected" data-testid="access-tab-rejected">Abgelehnt</TabsTrigger>
        </TabsList>
      </Tabs>
      <div className="mt-4 bg-white rounded-2xl border border-slate-200 px-4 sm:px-6">
        {items === null ? <div className="py-10 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
          : items.length === 0 ? <p className="py-10 text-center text-slate-500" data-testid="access-requests-empty">Keine Anfragen.</p>
          : <ul className="divide-y divide-slate-100">{items.map((r) => <RequestRow key={r.id} r={r} onDecide={decide} busy={busy} />)}</ul>}
      </div>
      <ApproveAccessDialog request={approving} busy={busy} onClose={() => setApproving(null)}
        onConfirm={(body) => decide(approving, "approve", body)} />
    </div>
  );
}
